import globalFormat from './GlobalFormat';
import moment from 'moment/moment';
import { Locale, TimeFormat } from '../apollo/graphqlSchema';
import { INumberFormatOptionProps } from './types';
import { MaybeNil } from '../interfaces';

const KOBOL_DATE_FORMAT_IN_ANALYSIS_DATA = 'M/D/YYYY';

// mapping Locale enum to locale string which is used by es5 number format function
export const localeDictionary: Record<Locale, string> = {
  [Locale.EnUs]: 'en-US',
  [Locale.EnGb]: 'en-GB',
  [Locale.FrFr]: 'fr-FR',
  [Locale.DeDe]: 'de-DE',
  [Locale.EsEs]: 'es-ES',
  [Locale.PtBr]: 'pt-BR',
  [Locale.DaDk]: 'da-DK',
  [Locale.EnCa]: 'en-CA',
  [Locale.EnIn]: 'en-IN',
  [Locale.EsMx]: 'es-MX',
  [Locale.ItIt]: 'it-IT',
};

// mapping TimeFormat enum to time format string which is used by moment.js
export const timeFormatDictionary: Record<TimeFormat, string> = {
  [TimeFormat.TwentyFourHour]: 'HH:mm:ss',
  [TimeFormat.TwelveHour]: 'h:mm:ss A',
};

export const isExponential = (value: number | string): boolean => {
  return value.toString().includes('e') || value.toString().includes('E');
};

export const isDateValid = (date: MaybeNil<Date | string>, strict?: boolean) =>
  !(!date || !moment(date, strict).isValid());

export const maximumFractionDigits = 8;

// common options for number formatting in GlobalFormat.numberFormat
export const defaultNumberFormatOptions: Record<
  string,
  INumberFormatOptionProps
> = {
  integer: {
    style: 'decimal',
    decimalPlace: 0,
  },
  decimal: {
    style: 'decimal',
    decimalPlace: 2,
  },
  compact: {
    style: 'decimal',
    notation: 'compact',
    decimalPlace: 0,
  },
  percent: {
    style: 'percent',
    decimalPlace: 2,
  },
  originDecimalPoint: {
    style: 'decimal',
    maximumFractionDigits,
    minimumFractionDigits: 0,
  },
  scientific: {
    style: 'decimal',
    notation: 'scientific',
    maximumFractionDigits,
    minimumFractionDigits: 0,
  },
};

// Used in Chart, because we don't know if the string is Key or date.
// so try to parse the text to date, if it's not a valid date, return the original string.
export const attemptFormatDate = (dateStr: string) => {
  // Special case: kobol sends M/D/YYYY ~ M/D/YYYY as date string for TimeUnit.AllTime in Expression and Funnel
  if (dateStr.includes('~')) {
    const dateStrArr = dateStr.split(' ~ ');
    if (
      dateStrArr.length === 2 &&
      moment(
        dateStrArr[0],
        KOBOL_DATE_FORMAT_IN_ANALYSIS_DATA,
        true,
      ).isValid() &&
      moment(dateStrArr[1], KOBOL_DATE_FORMAT_IN_ANALYSIS_DATA, true).isValid()
    ) {
      const { dateFormat } = globalFormat.getGlobalFormat();
      return (
        `${globalFormat.formatDate(dateStrArr[0], { dateFormat })}` +
        ` ~ ${globalFormat.formatDate(dateStrArr[1], { dateFormat })}`
      );
    }
  }

  if (moment(dateStr, KOBOL_DATE_FORMAT_IN_ANALYSIS_DATA, true).isValid()) {
    const { dateFormat } = globalFormat.getGlobalFormat();
    return globalFormat.formatDate(dateStr, { dateFormat });
  } else {
    return dateStr;
  }
};
